import { RootState } from "..";
import { ActionTree } from "vuex";
import { AdminState } from "./admin.module";
import { adminService } from "@/http/admin";
import { MUTATIONS } from "@/constants/mutations";
import store from "..";
import { CategoryData } from "@/interfaces/category-data.model";
import router from "@/router";

export const ADMIN_ACTIONS: ActionTree<AdminState, RootState> = {
  // categories
  addCategory(context): void {
    context.rootState.app.loading = true;

    const data = context.state.categoryData;

    adminService
      .addCategory(data)
      .then((res) => {
        // set snackbar
        store.commit("app/showSnackbar", res.data);

        context.rootState.app.loading = false;

        // move back
        router.push({ name: "admin-all-categories" });

        // reset category data
        context.commit(MUTATIONS.admin.resetCategoryData);

        // refresh categories
        store.dispatch("panel/getCategories");
      })
      .catch((error) => {
        // set snackbar
        store.commit("app/showSnackbar", error.response.data);

        context.rootState.app.loading = false;
      });
  },
  editCategory(context, payload: { id: string; data: CategoryData }): void {
    context.rootState.app.loading = true;

    adminService
      .updateCategory(payload.id, payload.data)
      .then((res) => {
        // set snackbar
        store.commit("app/showSnackbar", res.data);

        context.rootState.app.loading = false;

        // move back
        router.push({ name: "admin-all-categories" });

        // reset category data
        context.commit(MUTATIONS.admin.resetCategoryData);

        // refresh categories
        store.dispatch("panel/getCategories");
      })
      .catch((error) => {
        // set snackbar
        store.commit("app/showSnackbar", error.response.data);

        context.rootState.app.loading = false;
      });
  },
  deleteCategory(_, id: string): void {
    adminService
      .deleteCategory(id)
      .then((res) => {
        // set snackbar
        store.commit("app/showSnackbar", res.data);
      })
      .catch((error) => {
        // set snackbar
        store.commit("app/showSnackbar", error.response.data);
      });
  },
  updateCategoryTitle(context, value: string): void {
    context.commit(MUTATIONS.admin.updateCategoryTitle, value);
  },
  updateCategoryParent(context, value: string | null): void {
    context.commit(MUTATIONS.admin.updateCategoryParent, value);
  },
  updateCategoryParentPath(context, value: string): void {
    context.commit(MUTATIONS.admin.updateCategoryParentPath, value);
  },
  // products
  getProductDetails(context, id: string): void {
    // get product details
    adminService
      .getProductDetails(id)
      .then((res) => {
        context.commit(MUTATIONS.admin.setProduct, res.data.data);

        // end page loading
        context.rootState.app.pageLoading = false;
      })
      .catch((error) => {
        // end page loading
        context.rootState.app.pageLoading = false;

        // set snackbar
        store.commit("app/showSnackbar", error.response.data);
      });
  },
  getProducts(context, id: any): void {
    // get products
    adminService
      .getProducts(id)
      .then((res) => {
        context.state.adminProducts = [];
        context.commit(MUTATIONS.admin.setProducts, res.data.data.products);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        // set snackbar
        store.commit("app/showSnackbar", error.response.data);
        context.rootState.app.loading = false;
      });
  },
  editProduct(context, { formData, id }): void {
    context.rootState.app.loading = true;

    const data = { ...context.state.productData, img: "" };

    adminService
      .editProduct({ id, data })
      .then((res) => {
        // upload product main image
        adminService
          .uploadProductImage(formData.image, id)
          .then(() => {
            // upload product other images
            adminService
              .uploadProductDetailImages(formData.images, id)
              .then((res) => {
                // set snackbar
                store.commit("app/showSnackbar", res.data);

                // stop loading
                context.rootState.app.loading = false;

                // move to previous page
                router.push({ name: "admin-all-products" });

                // reset product data
                context.commit(MUTATIONS.admin.resetProductData);
              })
              .catch((error) => {
                store.commit("app/showSnackbar", error.response.data);

                // stop loading
                context.rootState.app.loading = false;
              });
          })
          .catch((error) => {
            store.commit("app/showSnackbar", error.response.data);
            context.rootState.app.loading = false;
          });
      })
      .catch((error) => {
        // set snackbar
        store.commit("app/showSnackbar", error.response.data);

        context.rootState.app.loading = false;
      });
  },
  addProduct(context, payload: { image: FormData; images: FormData }): void {
    context.rootState.app.loading = true;

    const data = { ...context.state.productData, img: "" };

    adminService
      .addProduct(data)
      .then((res) => {
        // send product image
        const prodId = res.data.data._id;
        if (prodId.length > 3) {
          // upload product main image
          adminService
            .uploadProductImage(payload.image, prodId)
            .then(() => {
              // upload product other images
              adminService
                .uploadProductDetailImages(payload.images, prodId)
                .then((res) => {
                  // set snackbar
                  store.commit("app/showSnackbar", res.data);
                  context.rootState.app.loading = false;

                  // move to previous page
                  router.push({ name: "admin-all-products" });

                  // reset product data
                  context.commit(MUTATIONS.admin.resetProductData);
                })
                .catch((error) => {
                  store.commit("app/showSnackbar", error.response.data);
                  context.rootState.app.loading = false;
                });
            })
            .catch((error) => {
              store.commit("app/showSnackbar", error.response.data);
              context.rootState.app.loading = false;
            });
        }
      })
      .catch((error) => {
        // set snackbar
        store.commit("app/showSnackbar", error.response.data);

        context.rootState.app.loading = false;
      });
  },
  deleteProduct(context, id: string): void {
    adminService
      .deleteProduct(id)
      .then((res) => {
        // delete the product from store
        context.commit(MUTATIONS.admin.deleteProduct, id);

        // set snackbar
        store.commit("app/showSnackbar", res.data);
      })
      .catch((error) => {
        // set snackbar
        store.commit("app/showSnackbar", error.response.data);
      });
  },
  createNewSubCategory(context): void {
    const data = {
      englishTitle: context.state.landing.subCategoryData.englishTitle,
      persianTitle: context.state.landing.subCategoryData.persianTitle,
      arabicTitle: context.state.landing.subCategoryData.arabicTitle,
    };

    const parentid = context.state.landing.subCategoryData.category;
    adminService
      .createSubCategory(data, parentid)
      .then((res) => {
        // delete the product from store
        // context.commit(MUTATIONS.admin.deleteProduct, id);

        // set snackbar
        store.commit("app/showSnackbar", res.data);
      })
      .catch((error) => {
        // set snackbar
        store.commit("app/showSnackbar", error.response.data);
      });
  },
  updateProductName(context, value: string): void {
    context.commit(MUTATIONS.admin.updateProductName, value);
  },
  updateProductCategory(context, value: string): void {
    context.commit(MUTATIONS.admin.updateProductCategory, value);
  },
  updateProductAmount(context, value: number): void {
    context.commit(MUTATIONS.admin.updateProductAmount, value);
  },
  updateProductSpecialSell(context, value: number): void {
    context.commit(MUTATIONS.admin.updateProductSpecialSell, value);
  },
  updateProductInventory(context, value: number): void {
    context.commit(MUTATIONS.admin.updateProductInventory, value);
  },
  updateProductDescription(context, value: string): void {
    context.commit(MUTATIONS.admin.updateProductDescription, value);
  },
  updateProductSpecial(context, value: boolean): void {
    context.commit(MUTATIONS.admin.updateProductSpecial, value);
  },
  updateProductNew(context, value: boolean): void {
    context.commit(MUTATIONS.admin.updateProductNew, value);
  },
  updateProductImage(context, value): void {
    context.commit(MUTATIONS.admin.updateProductImage, value);
  },
  // landing
  updateMainBannerAboutUs(context, value): void {
    context.commit(MUTATIONS.admin.updateMainBannerAboutUs, value);
  },

  updateAboutUsTitle(context, title: string): void {
    context.commit(MUTATIONS.admin.updateAboutUsTitle, title);
  },
  updateAboutUsLanguage(context, language: string): void {
    context.commit(MUTATIONS.admin.updateAboutUsLanguage, language);
  },
  updateAboutUsDescription(context, description: string): void {
    context.commit(MUTATIONS.admin.updateAboutUsDescription, description);
  },

  // aluminium language
  updateAluminiumLanguage(context, language: string): void {
    context.commit(MUTATIONS.admin.updateAluminiumLanguage, language);
  },
  updateAluminiumTitle(context, title: string): void {
    context.commit(MUTATIONS.admin.updateAluminiumTitle, title);
  },
  // potlid category
  updatePotlidLanguage(context, language: string): void {
    context.commit(MUTATIONS.admin.updatePotlidLanguage, language);
  },
  updatePotlidTitle(context, title: string): void {
    context.commit(MUTATIONS.admin.updatePotlidTitle, title);
  },
  // contact us
  updateContactUsTitle(context, title: string): void {
    context.commit(MUTATIONS.admin.updateContactUsTitle, title);
  },
  updateContactUsSubTitle(context, subTitle: string): void {
    context.commit(MUTATIONS.admin.updateContactUsSubTitle, subTitle);
  },
  updateContactUsAddress(context, address: string): void {
    context.commit(MUTATIONS.admin.updateContactUsAddress, address);
  },
  updateContactUsEmail(context, email: string): void {
    context.commit(MUTATIONS.admin.updateContactUsEmail, email);
  },
  updateContactUsPhone(context, phone: string): void {
    context.commit(MUTATIONS.admin.updateContactUsPhone, phone);
  },
  updateContactUsLanguage(context, language: string): void {
    context.commit(MUTATIONS.admin.updateContactUsLanguage, language);
  },
  // major shopping
  updateMajorShoppingLanguage(context, language: string): void {
    context.commit(MUTATIONS.admin.updateMajorShoppingLanguage, language);
  },
  updateMajorShoppingTitle(context, title: string): void {
    context.commit(MUTATIONS.admin.updateMajorShoppingTitle, title);
  },
  updateMajorShoppingDescription(context, description: string): void {
    context.commit(MUTATIONS.admin.updateMajorShoppingDescription, description);
  },
  updateMajorShoppingSubTitle(context, subTitle: string): void {
    context.commit(MUTATIONS.admin.updateMajorShoppingSubTitle, subTitle);
  },
  updateMainBannerMajorShopping(context, mainBanner: string): void {
    context.commit(MUTATIONS.admin.updateMainBannerMajorShopping, mainBanner);
  },
  // faqs
  // updateFaqDataTitle(context, title: string): void {
  //   console.log("title in action")
  //   context.commit(MUTATIONS.admin.updateFaqDataTitle, title);
  // },

  updateFaqDataTitle(context, title: string): void {
    context.commit(MUTATIONS.admin.updateFaqDataTitle, title);
  },
  updateFaqDataLanguage(context, language: string): void {
    context.commit(MUTATIONS.admin.updateFaqDataLanguage, language);
  },
  updateFaqDataDescription(context, description: string): void {
    context.commit(MUTATIONS.admin.updateFaqDataDescription, description);
  },
  // offer
  updateOfferDataTitle(context, title: string): void {
    context.commit(MUTATIONS.admin.updateOfferDataTitle, title);
  },
  updateOfferDataLanguage(context, language: string): void {
    context.commit(MUTATIONS.admin.updateOfferDataLanguage, language);
  },
  updateOfferDataDescription(context, description: string): void {
    context.commit(MUTATIONS.admin.updateOfferDataDescription, description);
  },
  
  // certificates
  updateCertificatesLanguage(context, language: string): void {
    context.commit(MUTATIONS.admin.updateCertificatesLanguage, language);
  },
  updateCertificatesTitle(context, title: string): void {
    context.commit(MUTATIONS.admin.updateCertificatesTitle, title);
  },
  updateCertificatesText(context, link: string): void {
    context.commit(MUTATIONS.admin.updateCertificatesText, link);
  },
  // catalogs
  updateCatalogLink(context, title: string): void {
    context.commit(MUTATIONS.admin.updateCatalogLink, title);
  },
  updateCatalogText(context, text: string): void {
    context.commit(MUTATIONS.admin.updateCatalogText, text);
  },
  updateCatalogLanguage(context, text: string): void {
    context.commit(MUTATIONS.admin.updateCatalogLanguage, text);
  },
  // home page
  updateMainBannerHomePage(context, mainBanner: string): void {
    context.commit(MUTATIONS.admin.updateMainBannerHomePage, mainBanner);
  },
  updateShopMainBanner(context, mainBanner: string): void {
    context.commit(MUTATIONS.admin.updateShopMainBanner, mainBanner);
  },
  updateMainBannerHomePageAboutUs(context, mainBanner: string): void {
    context.commit(MUTATIONS.admin.updateMainBannerHomePageAboutUs, mainBanner);
  },
  updateVideoPage(context, position: string): void {
    context.commit(MUTATIONS.admin.updateVideoPage, position);
  },
  updateVideoLink(context, video: string): void {
    context.commit(MUTATIONS.admin.updateVideoLink, video);
  },
  updateVideoDataTitle(context, title: string): void {
    context.commit(MUTATIONS.admin.updateVideoDataTitle, title);
  },
  updateVideoDataSubTitle(context, subTitle: string): void {
    context.commit(MUTATIONS.admin.updateVideoDataSubTitle, subTitle);
  },
  updateVideoDataDescription(context, description: string): void {
    context.commit(MUTATIONS.admin.updateVideoDataDescription, description);
  },
  // footer address
  updateFooterAddressLanguage(context, language: string): void {
    context.commit(MUTATIONS.admin.updateFooterAddressLanguage, language);
  },
  updateFooterAddressAddress(context, address: string): void {
    context.commit(MUTATIONS.admin.updateFooterAddressAddress, address);
  },
  updateFooterAddressPhone(context, phone: string): void {
    context.commit(MUTATIONS.admin.updateFooterAddressPhone, phone);
  },
  updateFooterAddressEmail(context, email: string): void {
    context.commit(MUTATIONS.admin.updateFooterAddressEmail, email);
  },
  // factory address
  updateFactoryAddressAddress(context, address: string): void {
    context.commit(MUTATIONS.admin.updateFactoryAddressAddress, address);
  },
  updateFactoryAddressLanguage(context, language: string): void {
    context.commit(MUTATIONS.admin.updateFactoryAddressLanguage, language);
  },
  // major shopping info
  updateMajorShoppingInfoLanguage(context, language: string): void {
    context.commit(MUTATIONS.admin.updateMajorShoppingInfoLanguage, language);
  },
  updateMajorShoppingInfoPhone(context, phone: string): void {
    context.commit(MUTATIONS.admin.updateMajorShoppingInfoPhone, phone);
  },
  updateMajorShoppingInfoEmail(context, email: string): void {
    context.commit(MUTATIONS.admin.updateMajorShoppingInfoEmail, email);
  },
  updateMajorShoppingInfoAddress(context, address: string): void {
    context.commit(MUTATIONS.admin.updateMajorShoppingInfoAddress, address);
  },
  updateHomePageAboutUsTitle(context, title: string): void {
    context.commit(MUTATIONS.admin.updateHomePageAboutUsTitle, title);
  },
  updateHomePageAboutUsSubTitle(context, subTitle: string): void {
    context.commit(MUTATIONS.admin.updateHomePageAboutUsSubTitle, subTitle);
  },
  updateHomePageAboutUsLanguage(context, language: string): void {
    context.commit(MUTATIONS.admin.updateHomePageAboutUsLanguage, language);
  },
  updateHomePageAboutUsDescription(context, description: string): void {
    context.commit(
      MUTATIONS.admin.updateHomePageAboutUsDescription,
      description
    );
  },
  updateHomePageStatueTitle(context, title: string): void {
    context.commit(MUTATIONS.admin.updateHomePageStatueTitle, title);
  },
  updateHomePageStatueLanguage(context, language: string): void {
    context.commit(MUTATIONS.admin.updateHomePageStatueLanguage, language);
  },
  updateHomePageStatueDescription(context, description: string): void {
    context.commit(
      MUTATIONS.admin.updateHomePageStatueDescription,
      description
    );
  },
  updateStatueImage(context, statueImage: string): void {
    context.commit(MUTATIONS.admin.updateStatueImage, statueImage);
  },

  // category and product in landing
  updateLandingProductName(context, title: string): void {
    context.commit(MUTATIONS.admin.updateLandingProductName, title);
  },
  updateLandingProductColor(context, title: string): void {
    context.commit(MUTATIONS.admin.updateLandingProductColor, title);
  },
  updateLandingProductSize(context, size: string): void {
    context.commit(MUTATIONS.admin.updateLandingProductSize, size);
  },
  updateLandingProductCategory(context, category: string): void {
    context.commit(MUTATIONS.admin.updateLandingProductCategory, category);
  },
  updateLandingProductType(context, type: string): void {
    context.commit(MUTATIONS.admin.updateLandingProductType, type);
  },
  updateLandingProductAmount(context, amount: string): void {
    context.commit(MUTATIONS.admin.updateLandingProductAmount, amount);
  },
  updateLandingProductDescription(context, description: string): void {
    context.commit(
      MUTATIONS.admin.updateLandingProductDescription,
      description
    );
  },
  updateLandingProductImage(context, value): void {
    context.commit(MUTATIONS.admin.updateLandingProductImage, value);
  },

  // landing category
  updateCategoryPersianTitle(context, persianTitle: string): void {
    context.commit(MUTATIONS.admin.updateCategoryPersianTitle, persianTitle);
  },
  updateSubCategoryPersianTitle(context, persianTitle: string): void {
    context.commit(MUTATIONS.admin.updateSubCategoryPersianTitle, persianTitle);
  },
  updateSubCategoryEnglishTitle(context, englishTitle: string): void {
    context.commit(MUTATIONS.admin.updateSubCategoryEnglishTitle, englishTitle);
  },
  updateSubCategoryArabicTitle(context, arabicTitle: string): void {
    context.commit(MUTATIONS.admin.updateSubCategoryArabicTitle, arabicTitle);
  },
  updateLandingSubCategory(context, subCategory: string): void {
    context.commit(MUTATIONS.admin.updateLandingSubCategory, subCategory);
  },
  updateCategoryType(context, categoryType: string): void {
    context.commit(MUTATIONS.admin.updateCategoryType, categoryType);
  },
  updateCategoryEnglishTitle(context, englishTitle: string): void {
    context.commit(MUTATIONS.admin.updateCategoryEnglishTitle, englishTitle);
  },
  updateCategoryArabicTitle(context, arabicTitle: string): void {
    context.commit(MUTATIONS.admin.updateCategoryArabicTitle, arabicTitle);
  },
  updateCategoryDescription(context, arabicTitle: string): void {
    context.commit(MUTATIONS.admin.updateCategoryDescription, arabicTitle);
  },
  updateCategoryImage(context, value): void {
    context.commit(MUTATIONS.admin.updateCategoryImage, value);
  },
  updateCategoryImageEdit(context, value): void {
    context.commit(MUTATIONS.admin.updateCategoryImageEdit, value);
  },
  // selected landing product
  updateSelectedLandingProductName(context, name: string): void {
    context.commit(MUTATIONS.admin.updateSelectedLandingProductName, name);
  },
  updateSelectedLandingProductCategory(context, category: string): void {
    context.commit(
      MUTATIONS.admin.updateSelectedLandingProductCategory,
      category
    );
  },
  updateSelectedLandingProductImage(context, image): void {
    context.commit(MUTATIONS.admin.updateSelectedLandingProductImage, image);
  },
  updateSelectedLandingProductType(context, type: string): void {
    context.commit(MUTATIONS.admin.updateSelectedLandingProductType, type);
  },
  updateSelectedLandingProductAmount(context, amount: number): void {
    context.commit(MUTATIONS.admin.updateSelectedLandingProductAmount, amount);
  },
  updateSelectedLandingProductDescription(context, description: number): void {
    context.commit(
      MUTATIONS.admin.updateSelectedLandingProductDescription,
      description
    );
  },
  updateSelectedLandingProductDescriptionSizes(context, sizes: any): void {
    context.commit(
      MUTATIONS.admin.updateSelectedLandingProductDescriptionSizes,
      sizes
    );
  },
  updateSelectedLandingProductDescriptionColors(context, colors: any): void {
    context.commit(
      MUTATIONS.admin.updateSelectedLandingProductDescriptionColors,
      colors
    );
  },

  submitAluminiumCategory(context): void {
    const data = context.state.landing.aluminiumCategory;
    context.rootState.app.loading = true;
    adminService
      .updateAluminiumCategoryApi(data)
      .then((res) => {
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.data);
        context.rootState.app.loading = false;
      });
  },
  submitPotlidCategory(context): void {
    const data = context.state.landing.potlidCategory;
    context.rootState.app.loading = true;
    adminService
      .updatePotlidCategoryApi(data)
      .then((res) => {
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.data);
        context.rootState.app.loading = false;
      });
  },

  submitFaqData(context): void {
    const data = context.state.landing.faqData;
    context.rootState.app.loading = true;
    adminService
      .createFaq(data)
      .then((res) => {
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.data);
        context.rootState.app.loading = false;
      });
  },

  getLandingFaqs(context): void {
    context.rootState.app.loading = true;
    adminService
      .getFaqs()
      .then((res) => {
        context.state.landing.faqs = res.data.data;
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.data);
        context.rootState.app.loading = false;
      });
  },

  deleteFaq(context, id: string): void {
    context.rootState.app.loading = true;
    adminService
      .deleteFaq(id)
      .then((res) => {
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
        router.push({ name: "admin" });
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.data);
        context.rootState.app.loading = false;
      });
  },

  getLandingOffers(context): void {
    context.rootState.app.loading = true;
    adminService
      .getOffers()
      .then((res) => {
        context.state.landing.offers = res.data.data;
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.data);
        context.rootState.app.loading = false;
      });
  },

  submitOfferData(context): void {
    const data = context.state.landing.offerData;
    context.rootState.app.loading = true;
    adminService
      .createOffer(data)
      .then((res) => {
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.data);
        context.rootState.app.loading = false;
      });
  },

  deleteOffer(context, id: string): void {
    context.rootState.app.loading = true;
    adminService
      .deleteOffer(id)
      .then((res) => {
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
        router.push({ name: "admin" });
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.data);
        context.rootState.app.loading = false;
      });
  },

  deleteLandingProduct(context, id: string): void {
    context.rootState.app.loading = true;
    adminService
      .deleteLandingProduct(id)
      .then((res) => {
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
        router.push({ name: "admin" });
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.data);
        context.rootState.app.loading = false;
      });
  },

  deleteFooterAddress(context, id: string): void {
    context.rootState.app.loading = true;
    adminService
      .deleteFooterAddress(id)
      .then((res) => {
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
        router.push({ name: "admin" });
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.data);
        context.rootState.app.loading = false;
      });
  },

  deleteFactoryAddress(context, id: string): void {
    context.rootState.app.loading = true;
    adminService
      .deleteFactoryAddress(id)
      .then((res) => {
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
        router.push({ name: "admin" });
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.data);
        context.rootState.app.loading = false;
      });
  },

  deleteMajorShoppingInfo(context, id: string): void {
    context.rootState.app.loading = true;
    adminService
      .deleteMajorShoppingInfo(id)
      .then((res) => {
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
        router.push({ name: "admin" });
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.data);
        context.rootState.app.loading = false;
      });
  },

  getLandingProductsByCategory(context, id: string): void {
    context.rootState.app.loading = true;
    adminService
      .findLandingProductsByCategory(id)
      .then((res) => {
        context.state.landing.landingProducts = res.data.data;
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.data);
        context.rootState.app.loading = false;
      });
  },

  getLandingProductById(context, id: string): void {
    // context.rootState.app.loading = true;
    adminService
      .findLandingProductById(id)
      .then((res) => {
        context.state.landing.selectedLandingProduct = res.data.data;
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.data);
        context.rootState.app.loading = false;
      });
  },

  getFooterAddresses(context, id: string): void {
    // context.rootState.app.loading = true;
    adminService
      .getFooterAddresses()
      .then((res) => {
        context.state.landing.footerAddresses = res.data.data;
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.data);
        context.rootState.app.loading = false;
      });
  },

  getFactoryAddresses(context, id: string): void {
    // context.rootState.app.loading = true;
    adminService
      .getFactoryAddresses()
      .then((res) => {
        context.state.landing.footerAddressFactories = res.data.data;
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.data);
        context.rootState.app.loading = false;
      });
  },

  getMajorShoppingInfos(context, id: string): void {
    // context.rootState.app.loading = true;
    adminService
      .getMajorShoppingInfos()
      .then((res) => {
        context.state.landing.majorShoppingInfos = res.data.data;
        console.log("major shopping infos", res.data.data);
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.data);
        context.rootState.app.loading = false;
      });
  },

  postEditLandingProduct(context, value: any): void {
    context.rootState.app.loading = true;
    const data = context.state.landing.selectedLandingProduct;
    const result = {
      name: data.name,
      category: data.category,
      type: data.type,
      amount: data.amount,
      description: data.description,
      sizes: data.sizes,
      colors: data.colors,
    };
    adminService
      .editLandingProduct(value.id, result)
      .then((res) => {
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
        adminService
          .uploadLandingProductImage(value.image, value.id)
          .then(() => {
            adminService
              .uploadLandingProductImages(value.images, value.id)
              .then((res) => {
                store.commit("app/showSnackbar", res.data);
                context.rootState.app.loading = false;
                router.push({ name: "admin" });
              })
              .catch((error) => {
                store.commit("app/showSnackbar", res.data);
                context.rootState.app.loading = false;
              });
          })
          .catch((error) => {
            store.commit("app/showSnackbar", error.response.data);
            context.rootState.app.loading = false;
          });
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.data);
        context.rootState.app.loading = false;
      });
  },
  // submitPotlidCategory(context): void {
  //   const data = context.state.landing.potlidCategory;
  //   context.rootState.app.loading = true;
  //   adminService
  //     .updatePotlidCategoryApi(data)
  //     .then((res) => {
  //       store.commit("app/showSnackbar", res.data);
  //       context.rootState.app.loading = false;
  //     })
  //     .catch((error) => {
  //       store.commit("app/showSnackbar", error.data);
  //       context.rootState.app.loading = false;
  //     });
  // },
  getMainBanners(context): void {
    context.rootState.app.loading = true;
    adminService
      .getMainBanners()
      .then((res) => {
        context.state.landing.mainBanners = res.data.data;
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.data);
        context.rootState.app.loading = false;
      });
  },

  submitEditVideoTitle(context, data: { _id: string; title: string }) {
    adminService
      .updateVideo(data._id, data.title)
      .then((response) => {
        store.commit("app/showSnackbar", response.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.data);
        context.rootState.app.loading = false;
      });
  },

  deleteMainBanner(context, id): void {
    context.rootState.app.loading = true;
    adminService
      .deleteMainBanner(id)
      .then((res) => {
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.data);
        context.rootState.app.loading = false;
      });
  },

  submitImageCategoryEdit(context, payload): void {
    context.rootState.app.loading = true;
    adminService
      .editLandingCategoryImage(payload.id, payload.image)
      .then((res) => {
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.data);
        context.rootState.app.loading = false;
      });
  },

  submitCreateVideo(context, data): void {
    context.rootState.app.loading = true;
    adminService
      .createVideo(data)
      .then((res) => {
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.data);
        context.rootState.app.loading = false;
      });
  },

  submitCreateFooterAddress(context, data): void {
    context.rootState.app.loading = true;
    adminService
      .createFooterAddress(data)
      .then((res) => {
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.data);
        context.rootState.app.loading = false;
      });
  },

  submitCreateFactoryAddress(context, data): void {
    context.rootState.app.loading = true;
    adminService
      .createFactoryAddress(data)
      .then((res) => {
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.data);
        context.rootState.app.loading = false;
      });
  },

  createMajorShoppingInfo(context, data): void {
    context.rootState.app.loading = true;
    adminService
      .createMajorShoppingInfo(data)
      .then((res) => {
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.data);
        context.rootState.app.loading = false;
      });
  },

  getLandingVideos(context): void {
    context.rootState.app.loading = true;
    adminService
      .getVideoHistory()
      .then((res) => {
        context.state.landing.videos = res.data.data;
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.data);
        context.rootState.app.loading = false;
      });
  },

  deleteVideo(context, id): void {
    context.rootState.app.loading = true;
    adminService
      .deleteVideo(id)
      .then((res) => {
        router.push({ name: "admin" });
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.data);
        context.rootState.app.loading = false;
      });
  },

  getLandingSubCategories(context): void {
    context.rootState.app.loading = true;
    adminService
      .getSubCategories()
      .then((res) => {
        context.state.landing.subCategories = res.data.data;
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.data);
        context.rootState.app.loading = false;
      });
  },

  deleteSubCategory(context, id: string): void {
    context.rootState.app.loading = true;
    adminService
      .deleteSubCategory(id)
      .then((res) => {
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
        adminService.getSubCategories().then((res) => {
          context.state.landing.subCategories = res.data.data;
          store.commit("app/showSnackbar", res.data);
          context.rootState.app.loading = false;
        });
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.data);
        context.rootState.app.loading = false;
      });
  },

  updateCategoryBanner(context, value): void {
    context.commit(MUTATIONS.admin.updateCategoryBanner, value);
  },
  updateBannerCategory(context, value): void {
    context.commit(MUTATIONS.admin.updateBannerCategory, value);
  },
  // more box
  updateMoreBoxContentTitle(context, title: string): void {
    context.commit(MUTATIONS.admin.updateMoreBoxContentTitle, title);
  },
  updateMoreBoxContentDescription(context, description: string): void {
    context.commit(
      MUTATIONS.admin.updateMoreBoxContentDescription,
      description
    );
  },
  updateMoreBoxContentImage(context, img): void {
    context.commit(MUTATIONS.admin.updateMoreBoxContentImage, img);
  },

  uploadAboutUsMainBanner(context, payload: { image: FormData }): void {
    context.rootState.app.loading = true;
    adminService
      .updateAboutUsMainBanner(payload.image)
      .then((res) => {
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.response.data);
        context.rootState.app.loading = false;
      });
  },

  submitAboutUsContent(context, data): void {
    adminService
      .updateAboutUsContent(data)
      .then((response) => {
        store.commit("app/showSnackbar", response.data);
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.response.data);
        context.rootState.app.loading = false;
      });
  },

  submitContactUsContent(context, data): void {
    context.rootState.app.loading = true;
    adminService
      .updateContactUsContent(data)
      .then((response) => {
        store.commit("app/showSnackbar", response.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.response.data);
        context.rootState.app.loading = false;
      });
  },
  submitCertificatesContent(context, data): void {
    context.rootState.app.loading = true;
    const result = {
      language: data.store.language,
      title: data.store.title,
      text: data.store.text,
    };
    adminService
      .updateCertificatesContent(result)
      .then((response) => {
        adminService
          .uploadCertificateImage(response.data.data._id, data.image)
          .then((res) => {
            store.commit("app/showSnackbar", res.data);
            context.rootState.app.loading = false;
          })
          .catch((error) => {
            store.commit("app/showSnackbar", error.response.data);
            context.rootState.app.loading = false;
          });
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.response.data);
        context.rootState.app.loading = false;
      });
  },
  submitCatalogContent(context, data): void {
    context.rootState.app.loading = true;
    const result = {
      // img: data.image,
      language: String(data.store.language).toLowerCase(),
      link: data.store.link,
      text: data.store.text,
    };

    adminService
      .submitCatalog(result)
      .then((response) => {
        adminService
          .uploadCatalogImage(response.data.data._id, data.image)
          .then((res) => {
            store.commit("app/showSnackbar", res.data);
            context.rootState.app.loading = false;
          })
          .catch((error) => {
            store.commit("app/showSnackbar", error.response.data);
            context.rootState.app.loading = false;
          });
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.response.data);
        context.rootState.app.loading = false;
      });
  },

  upadetCertificateImage(context, img): void {
    context.commit(MUTATIONS.admin.upadetCertificateImage, img);
  },
  upadetCatalogImage(context, img): void {
    context.commit(MUTATIONS.admin.upadetCatalogImage, img);
  },

  uploadMajorShoppingMainBanner(context, payload: { image: FormData }): void {
    context.rootState.app.loading = true;
    adminService
      .uploadMajorShoppingMainBanner(payload.image)
      .then((res) => {
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.response.data);
        context.rootState.app.loading = false;
      });
  },

  uploadStatueImageFile(context, payload: { image: FormData }): void {
    context.rootState.app.loading = true;
    adminService
      .uploadStatueImage(payload.image)
      .then((res) => {
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.response.data);
        context.rootState.app.loading = false;
      });
  },

  submitMajorShoppingContent(context, data): void {
    adminService
      .updateMajorShoppingContent(data)
      .then((response) => {
        store.commit("app/showSnackbar", response.data);
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.response.data);
        context.rootState.app.loading = false;
      });
  },

  submitBannerCategory(
    context,
    payload: { image: FormData; category: string }
  ): void {
    context.rootState.app.loading = true;
    adminService
      .uploadCategoryService(payload.image, payload.category)
      .then((response) => {
        store.commit("app/showSnackbar", response.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.response.data);
        context.rootState.app.loading = false;
      });
  },

  getServicesById(context, category: string): void {
    adminService
      .getServicesByCategory(category)
      .then((response) => {
        context.state.landing.categoryServices = response.data.data;
        store.commit("app/showSnackbar", response.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.response.data);
        context.rootState.app.loading = false;
      });
  },
  createLandingCategory(
    context,
    payload: { image: FormData; banner: FormData }
  ): void {
    context.rootState.app.loading = true;

    const data = {
      img: "",
      mainBanner: "",
      description: context.state.landing.categoryData.description,
      englishTitle: context.state.landing.categoryData.englishTitle,
      persianTitle: context.state.landing.categoryData.persianTitle,
      arabicTitle: context.state.landing.categoryData.arabicTitle,
      type: context.state.landing.categoryData.categoryType,
      imageServices: [],
    };
    adminService
      .createLandingCategory(data)
      .then((res) => {
        // send product image
        const prodId = res.data.data._id;
        if (prodId.length > 3) {
          // upload product main image
          adminService
            .uploadLandingCategoryImage(payload.image, prodId)
            .then(() => {
              // upload product other images
              adminService
                .uploadLandingCategoryBanner(payload.banner, prodId)
                .then((res) => {
                  // set snackbar
                  store.commit("app/showSnackbar", res.data);
                  context.rootState.app.loading = false;

                  // move to previous page
                  // router.push({ name: "admin-all-products" });

                  // reset product data
                  // context.commit(MUTATIONS.admin.resetProductData);
                })
                .catch((error) => {
                  store.commit("app/showSnackbar", error.response.data);
                  context.rootState.app.loading = false;
                });
            })
            .catch((error) => {
              store.commit("app/showSnackbar", error.response.data);
              context.rootState.app.loading = false;
            });
        }
      })
      .catch((error) => {
        // set snackbar
        store.commit("app/showSnackbar", error.response.data);

        context.rootState.app.loading = false;
      });
  },
  createLandingProduct(
    context,
    payload: { image: FormData; banner: FormData; images: FormData }
  ): void {
    context.rootState.app.loading = true;

    const data = {
      img: "",
      name: context.state.landing.productData.name,
      category: context.state.landing.productData.category,
      type: context.state.landing.productData.type,
      amount: context.state.landing.productData.amount,
      description: context.state.landing.productData.description,
      sizes: context.state.landing.productData.sizes,
      colors: context.state.landing.productData.colors,
      imges: [],
    };

    adminService
      .createLandingProduct(data)
      .then((res) => {
        // send product image
        const prodId = res.data.data._id;
        if (prodId.length > 3) {
          // upload product main image
          adminService
            .uploadLandingProductImage(payload.image, prodId)
            .then(() => {
              // upload product other images
              // adminService
              //   .uploadLandingCategoryBanner(payload.banner, prodId)
              //   .then((res) => {
              // set snackbar
              adminService
                .uploadLandingProductImages(payload.images, prodId)
                .then((res) => {
                  store.commit("app/showSnackbar", res.data);
                  context.rootState.app.loading = false;
                })
                .catch((error) => {
                  store.commit("app/showSnackbar", res.data);
                  context.rootState.app.loading = false;
                });

              // move to previous page
              // router.push({ name: "admin-all-products" });

              // reset product data
              // context.commit(MUTATIONS.admin.resetProductData);
              // })
              // .catch((error) => {
              //   store.commit("app/showSnackbar", error.response.data);
              //   context.rootState.app.loading = false;
              // });
            })
            .catch((error) => {
              store.commit("app/showSnackbar", error.response.data);
              context.rootState.app.loading = false;
            });
        }
      })
      .catch((error) => {
        // set snackbar
        store.commit("app/showSnackbar", error.response.data);

        context.rootState.app.loading = false;
      });
  },

  submitMoreBoxContent(context, data): void {
    const moreBoxContent = {
      title: context.state.landing.moreBoxContent.title,
      description: context.state.landing.moreBoxContent.description,
    };
    const image = data.image;
    context.rootState.app.loading = true;
    adminService
      .createNewLandingMoreBox(moreBoxContent)
      .then((res) => {
        const moreBoxId = res.data.data._id;
        if (moreBoxId.length > 3) {
          adminService
            .uploadMoreBoxImage(image, moreBoxId)
            .then((res) => {
              store.commit("app/showSnackbar", res.data);
              context.rootState.app.loading = false;
            })
            .catch((error) => {
              store.commit("app/showSnackbar", res.data);
              context.rootState.app.loading = false;
            });
        }
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.response.data);

        context.rootState.app.loading = false;
      });
  },

  getLandingMoreBoxes(context): void {
    adminService
      .getMoreBoxes()
      .then((res) => {
        context.state.landing.moreBoxes = res.data.data;

        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.response.data);
      });
  },

  getContactUsData(context) {
    adminService
      .getContactUs()
      .then((res) => {
        context.state.landing.contactUs = res.data.data[0].aboutUs.contactUs.Fa;

        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.response.data);
      });
  },

  getShopMainBanners(context) {
    adminService
      .getShopMainBanners()
      .then((res) => {
        context.state.landing.shopMainBanners = res.data.data;
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.response.data);
      });
  },

  createShopMainBanner(
    context,
    payload: { image: FormData; category: string }
  ): void {
    context.rootState.app.loading = true;
    adminService
      .createShopMainBanner(payload.image)
      .then((response) => {
        store.commit("app/showSnackbar", response.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.response.data);
        context.rootState.app.loading = false;
      });
  },

  deleteShopMainBanner(context, id: string): void {
    context.rootState.app.loading = true;
    adminService
      .deleteShopMainBanner(id)
      .then((res) => {
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
        router.push({ name: "admin" });
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.data);
        context.rootState.app.loading = false;
      });
  },

  getCertificates(context): void {
    adminService
      .getCertificates()
      .then((res) => {
        context.state.landing.certificatesList = res.data.data;

        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.response.data);
      });
  },

  getCatalogs(context): void {
    adminService
      .getCatalogs()
      .then((res) => {
        context.state.landing.catalogs = res.data.data;

        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.response.data);
      });
  },

  deleteCatalog(context, id: string): void {
    adminService
      .deleteCatalog(id)
      .then((res) => {
        context.rootState.app.loading = false;
        router.push({ name: "admin" });
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.response.data);
      });
  },

  deleteCertificate(context, id: string): void {
    adminService
      .deleteCertificate(id)
      .then((res) => {
        context.rootState.app.loading = false;
        router.push({ name: "admin" });
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.response.data);
      });
  },

  deleteCategoryService(context, id: string): void {
    adminService
      .deleteCategoryService(id)
      .then((res) => {
        context.rootState.app.loading = false;
        router.push({ name: "admin" });
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.response.data);
      });
  },

  deleteMoreBox(context, id: string): void {
    adminService
      .deleteMoreBox(id)
      .then((res) => {
        context.rootState.app.loading = false;
        store.commit("app/showSnackbar", "عملیات با موفقیت انجام شد");
        router.push({ name: "admin" });
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.response.data);
      });
  },
  deleteLandingCategory(context, id: string): void {
    adminService
      .deleteLandingCategory(id)
      .then((res) => {
        context.rootState.app.loading = false;
        router.push({ name: "admin" });
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.response.data);
      });
  },
  // home page
  uploadHomePageMainBanner(context, payload: { image: FormData }): void {
    context.rootState.app.loading = true;
    adminService
      .updateHomePageMainBanner(payload.image)
      .then((res) => {
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.response.data);
        context.rootState.app.loading = false;
      });
  },
  uploadHomePageAboutUsMainBanner(context, payload: { image: FormData }): void {
    context.rootState.app.loading = true;
    adminService
      .updateHomePageAboutUsMainBanner(payload.image)
      .then((res) => {
        store.commit("app/showSnackbar", res.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.response.data);
        context.rootState.app.loading = false;
      });
  },
  submitHomePageAboutUsContent(context, data): void {
    context.rootState.app.loading = true;
    adminService
      .updateHomePageAboutUsContent(data)
      .then((response) => {
        store.commit("app/showSnackbar", response.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.response.data);
        context.rootState.app.loading = false;
      });
  },
  submitHomePageStatueContent(context, data): void {
    context.rootState.app.loading = true;
    adminService
      .updateStatueContent(data)
      .then((response) => {
        store.commit("app/showSnackbar", response.data);
        context.rootState.app.loading = false;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.response.data);
        context.rootState.app.loading = false;
      });
  },

  getAllCategories(context): void {
    adminService
      .getAllLandingCategories()
      .then((res) => {
        context.state.landing.categories = res.data.data;
        const categories = context.state.landing.categories;
      })
      .catch((error) => {
        console.log("get categories error:::", error);
      });
  },
  // get users list
  getUsersList(context): void {
    adminService
      .getUsersList()
      .then((res) => {
        // set users list in store
        context.commit(MUTATIONS.admin.setUsersList, res.data.data.usersList);

        // end page loading
        context.rootState.app.pageLoading = false;

        // set snackbar
        store.commit("app/showSnackbar", res.data);
      })
      .catch((error) => {
        // end page loading
        context.rootState.app.pageLoading = false;

        // set snackbar
        store.commit("app/showSnackbar", error.response.data);
      });
  },

  // get statues
  getStatues(context) {
    adminService
      .getStatues()
      .then((response) => {
        context.state.landing.homePage.statues = response.data.data;
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.response.data);
        context.rootState.app.loading = false;
      });
  },
  // delete statue
  deleteStatue(context, id: string) {
    adminService
      .deleteStatue(id)
      .then((response) => {
        store.commit("app/showSnackbar", response.data);
        router.push({ name: "admin" });
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.response.data);
        context.rootState.app.loading = false;
      });
  },
  // set user profile data on state
  setUserProfile(context, data): void {
    context.commit(MUTATIONS.admin.setUserProfile, data);
  },
  setUserFullname(context, value: string) {
    context.commit(MUTATIONS.admin.setUserFullname, value);
  },
  setUserAddress(context, value: string) {
    context.commit(MUTATIONS.admin.setUserAddress, value);
  },
  setUserProvince(context, value: string) {
    context.commit(MUTATIONS.admin.setUserProvince, value);
  },
  setUserCity(context, value: string) {
    context.commit(MUTATIONS.admin.setUserCity, value);
  },
  setUserAvatar(context, value: string) {
    context.commit(MUTATIONS.admin.setUserAvatar, value);
  },
  removeUserAvatar(context) {
    context.commit(MUTATIONS.admin.removeUserAvatar);
  },

  // edit user profile by id
  editUserProfile(_, id): void {
    const data = store.state.admin.userProfile;
    adminService
      .editUserProfile(id, data)
      .then((res) => {
        store.commit("app/showSnackbar", res.data);
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.response.data);
      });
  },
  editUserAvatar(_, { formData, id }): void {
    adminService
      .editUserAvatar(formData, id)
      .then((res) => {
        store.commit("app/showSnackbar", res.data);

        // refresh
        store.dispatch("admin/getUsersList");
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.response.data);
      });
  },
  // set amount wallet user charge
  setAmountWalletCharge(context, value: number): void {
    context.commit(MUTATIONS.admin.setAmountWalletCharge, value);
  },
  // charge user wallet (request)
  chargingTheUserWallet(context, userId): void {
    const amount = Number(context.state.amountWalletCharge.amount);
    adminService
      .chargingTheUserWallet(amount, userId)
      .then((res) => {
        store.commit("app/showSnackbar", res.data);

        // refresh
        store.dispatch("admin/getUsersList");
      })
      .catch((error) => {
        // set snackbar
        store.commit("app/showSnackbar", error.response.data);
      });
  },
  // get transaction list (request)
  getTransactionsListPanelAdmin(context): void {
    context.rootState.app.pageLoading = false;
    adminService
      .getTransactionsList()
      .then((res) => {
        // end page loading
        context.rootState.app.pageLoading = false;

        // set transactions in store model
        context.state.transactionsList = res.data.data.transactions;
        store.commit("app/showSnackbar", res.data);
      })
      .catch((error) => {
        // end page loading
        context.rootState.app.pageLoading = false;
        store.commit("app/showSnackbar", error.response.data);
      });
  },
  // get invoices list (request)
  getInvoicesList(context): void {
    adminService
      .getInvoicesList()
      .then((res) => {
        // set invoices list in store model
        context.commit(
          MUTATIONS.admin.setInvoicesList,
          res.data.data.invoicesList
        );

        // end page loading
        context.rootState.app.pageLoading = false;
      })
      .catch((error) => {
        // end page loading
        context.rootState.app.pageLoading = false;

        store.commit("app/showSnackbar", error.response.data);
      });
  },
  // accept invoice
  acceptInvoice(_, id): void {
    adminService
      .acceptInvoice(id)
      .then((res) => {
        store.commit("app/showSnackbar", res.data);

        // re-fetch invoices
        store.dispatch("admin/getInvoicesList");
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.response.data);
      });
  },
  // end invoice
  endInvoice(_, id): void {
    adminService
      .endInvoice(id)
      .then((res) => {
        store.commit("app/showSnackbar", res.data);

        // re-fetch invoices
        store.dispatch("admin/getInvoicesList");
      })
      .catch((error) => {
        store.commit("app/showSnackbar", error.response.data);
      });
  },
  // admins
  getAdminsList(context): void {
    adminService
      .getAdminsList()
      .then((res) => {
        context.commit(MUTATIONS.admin.setAdminsList, res.data.data.adminsList);

        // end page loading
        context.rootState.app.pageLoading = false;

        store.commit("app/showSnackbar", res.data);
      })
      .catch((error) => {
        // end page loading
        context.rootState.app.pageLoading = false;

        store.commit("app/showSnackbar", error.response.data);
      });
  },

  // refactor admin actions
  getAllLandingData(context): void {
    context.rootState.app.pageLoading = true;
    adminService
      .getAllLandingData()
      .then((res) => {
        // major shopping infos
        context.state.landing.majorShopping.orginalBanner = res.data.data[0].majorShopping.mainBanner.img;
        context.state.landing.majorShopping.title = res.data.data[0].majorShopping.contentBox.Fa.title;
        context.state.landing.majorShopping.subTitle = res.data.data[0].majorShopping.contentBox.Fa.subTitle;
        context.state.landing.majorShopping.text = res.data.data[0].majorShopping.contentBox.Fa.text;
        // context.state.landing.majorShopping.contentBox.En = res.data.data[0].majorShopping.contentBox.En;
        // context.state.landing.majorShopping.contentBox.Ar = res.data.data[0].majorShopping.contentBox.Ar;

        // abous us
        context.state.landing.aboutUs.orginalBanner = res.data.data[0].aboutUs.mainBanner.img;
        console.log("about us", res.data.data[0].aboutUs);
        context.state.landing.aboutUs.title = res.data.data[0].aboutUs.historyText.Fa.title;
        context.state.landing.aboutUs.description = res.data.data[0].aboutUs.historyText.Fa.text;
        // context.state.landing.aboutUs.text = res.data.data[0].aboutUs.contentBox.Fa.text;

        context.rootState.app.pageLoading = false;
        store.commit("app/showSnackbar", res.data);
      })
      .catch((error) => {
        context.rootState.app.pageLoading = false;
        store.commit("app/showSnackbar", error.response.data);
      });
  }
};
